"use client";

import { useEffect, useMemo, useState } from "react";
import {
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Field,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  INPUT_MONO,
  LABEL,
  SELECT,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  ExplanationList,
  MatchTable,
} from "@/components/tools/developer/regex-parts";
import { useRegexRunner } from "@/components/tools/developer/use-regex-runner";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { explainRegex } from "@/lib/tool-kit/developer/regex/explain";
import {
  type LibraryPatternId,
  libraryPattern,
  REGEX_LIBRARY,
} from "@/lib/tool-kit/developer/regex/library";
import { type RegexJob, runRegexJob } from "@/lib/tool-kit/developer/regex/run";

/** English strings. A translated content file overrides these through `ui`. */
export const REGEX_TESTER_STRINGS = {
  patternLabel: "Regular expression",
  patternHint:
    "The pattern without its surrounding slashes, in JavaScript syntax — the regex engine is your browser's own.",
  literal: "As a JavaScript literal: {literal}",
  copyPattern: "Copy literal",
  flagsLabel: "Flags",
  flags: {
    d: "indices — report each group's position",
    g: "global — find every match",
    i: "ignore case",
    m: "multiline — ^ and $ match at line breaks",
    s: "dotAll — . also matches line breaks",
    u: "unicode — whole code points, \\u{…} and \\p{…}",
    v: "unicodeSets — set operations inside [ ]",
    y: "sticky — match only where the last match ended",
  },
  libraryLabel: "Load a common pattern",
  libraryPlaceholder: "Choose a pattern…",
  libraryNames: {
    isoDate: "ISO 8601 date (YYYY-MM-DD)",
    email: "Email address",
    url: "Web address (http or https)",
    doi: "DOI",
    isbn: "ISBN-10 or ISBN-13, one per line",
    usPhone: "US phone number",
    citation: "APA in-text citation",
    doubledWord: "Doubled word",
    doubleSpace: "Double spaces",
  },
  libraryNotes: {
    isoDate:
      "Checks months 01–12 and days 01–31, but not the length of each month: 2024-02-31 still matches.",
    email:
      "Catches everyday addresses. No regular expression implements the email standard in full: quoted local parts and non-Latin domains are missed, and some invalid addresses pass.",
    url: "Leaves a trailing full stop, comma or closing parenthesis out of the match, so a URL that really ends in “)” loses it.",
    doi: "Crossref's recommended pattern for modern DOIs. A full stop ending the sentence is taken as part of the DOI, and a few early DOIs use characters outside the pattern.",
    isbn: "Checks the shape of an ISBN, not its check digit — confirm the number with an ISBN converter.",
    usPhone:
      "North American numbers: the area code and exchange cannot start with 0 or 1, and the area code's middle digit is not 9. Extensions are not matched.",
    citation:
      "One source per pair of parentheses. Page numbers, several sources together and narrative citations need patterns of their own.",
    doubledWord:
      "Also flags legitimate repeats such as “had had”, so read each one before replacing.",
    doubleSpace:
      "Two or more spaces in a row; the replacement leaves a single space.",
  },
  librarySamples: {
    isoDate:
      "Submitted 2024-03-15, revised 2024-11-02; the 2024-13-01 typo is not a date.",
    email:
      "Contact j.doe+lab@uni.example.ac.uk or the office (office@example.org), not @someone.",
    url: "Read https://example.org/report?id=42. Mirror: (http://archive.example.net/report).",
    doi: "See doi:10.1038/nphys1170 and https://doi.org/10.1000/xyz123 for the data.",
    isbn: "ISBN 978-0-596-52068-7\n0-596-52068-9\n978-0-596-52068\nISBN-13: 9780596520687",
    usPhone:
      "Call (415) 555-2671 or +1 212.555.0199 — not 123-456-7890, which is not a valid number.",
    citation:
      "Sleep aids recall (Walker, 2017), though see (Smith & Jones, 2019a) and (Lee et al., n.d.).",
    doubledWord: "It is is the end of the the chapter.",
    doubleSpace: "Two spaces  after a full stop.  An old typing habit.",
  },
  textLabel: "Test text",
  replaceToggle: "Preview a replacement",
  replaceLabel: "Replacement",
  replaceHint:
    "$1 or $<name> inserts a group, $& the whole match, $$ a dollar sign.",
  results: "Matches",
  matchCount: "one: {count} match|other: {count} matches",
  noMatch: "No match.",
  running: "Matching…",
  timeout:
    "Stopped after {seconds} seconds. The pattern probably backtracks catastrophically — look for a repeated group that itself contains + or *, such as (a+)+. Matching runs in a background worker, so the page stays responsive.",
  unavailable:
    "This browser would not start the background worker that stops a runaway pattern from freezing the page.",
  runHere: "Run once on this page anyway",
  invalid: "Not a valid pattern: {message}",
  truncated: "Only the first {count} matches are listed.",
  nested:
    "Nested quantifiers: a group containing + or * that is itself repeated can take exponentially long on text that almost matches.",
  highlightLabel: "Test text with the matches highlighted",
  tableHeads: ["#", "Match", "Position", "Groups"],
  position: "{start}–{end}",
  groupNumbered: "{group}: {value}",
  groupNamed: "{name}: {value}",
  groupUnmatched: "(no match)",
  emptyMatch: "(empty)",
  replacedLabel: "Text after replacement",
  copyReplaced: "Copy result",
  copied: "Copied!",
  explanationHeading: "What the pattern says",
  explanationEmpty: "Type a pattern to see it explained, token by token.",
  explain: {
    literal: "The text “{text}”",
    any: "Any character except a line break",
    anyDotAll: "Any character, line breaks included",
    digit: "A digit, 0–9",
    notDigit: "Any character except a digit",
    word: "A word character: a letter, digit or underscore",
    notWord: "Any character except a letter, digit or underscore",
    space: "A whitespace character",
    notSpace: "Any character except whitespace",
    boundary: "A word boundary",
    notBoundary: "Not a word boundary",
    start: "The start of the text",
    startLine: "The start of a line",
    end: "The end of the text",
    endLine: "The end of a line",
    escapedChar: "The character “{text}” itself",
    codePoint: "The character {code}",
    control: "The control character Ctrl+{letter}",
    property: "A character with the Unicode property {name}",
    notProperty: "A character without the Unicode property {name}",
    class: "One character from: {items}",
    negatedClass: "One character not in: {items}",
    group: "Start of capture group {group}",
    namedGroup: "Start of capture group {group}, named “{name}”",
    nonCapturing: "Start of a group that does not capture",
    modifiers: "Start of a group with its own flags (on: {on}; off: {off})",
    lookahead: "Start of a lookahead: what follows must match",
    negativeLookahead:
      "Start of a negative lookahead: what follows must not match",
    lookbehind: "Start of a lookbehind: what comes before must match",
    negativeLookbehind:
      "Start of a negative lookbehind: what comes before must not match",
    groupEnd: "End of the group",
    alternation: "Or: the alternative on the right is tried next",
    backreference: "The same text that group {group} matched",
    namedBackreference: "The same text that group “{name}” matched",
    zeroOrMore: "Zero or more times",
    oneOrMore: "One or more times",
    optional: "Optional: zero times or once",
    unknown: "Not recognised by this explainer",
  },
  explainExactly: "one: Exactly {count} time|other: Exactly {count} times",
  explainAtLeast: "one: At least {count} time|other: At least {count} times",
  explainBetween:
    "one: Between {min} and {count} time|other: Between {min} and {count} times",
  lazy: "{rule}, as few as possible",
  specialNames: {
    lineFeed: "A line feed (new line)",
    carriageReturn: "A carriage return",
    tab: "A tab",
    formFeed: "A form feed",
    verticalTab: "A vertical tab",
    nul: "The NUL character",
  },
  tooLong: "Only the first {limit} characters of the test text are searched.",
} as const;

export type RegexTesterStrings = typeof REGEX_TESTER_STRINGS;

type FlagKey = keyof RegexTesterStrings["flags"];

/** In the order `RegExp.prototype.flags` writes them. */
const FLAG_ORDER: readonly FlagKey[] = ["d", "g", "i", "m", "s", "u", "v", "y"];
const TIMEOUT_MS = 1500;
const MAX_MATCHES = 5000;
const MAX_LISTED = 200;
const MAX_TEXT = 200_000;
const DEFAULT = libraryPattern("isoDate");

function flagsFrom(set: ReadonlySet<FlagKey>): string {
  return FLAG_ORDER.filter((flag) => set.has(flag)).join("");
}

/** `u` and `v` cannot be combined; turning one on turns the other off. */
function toggleFlag(
  set: ReadonlySet<FlagKey>,
  flag: FlagKey,
  on: boolean
): Set<FlagKey> {
  const next = new Set(set);
  if (on) {
    next.add(flag);
    if (flag === "u") {
      next.delete("v");
    } else if (flag === "v") {
      next.delete("u");
    }
  } else {
    next.delete(flag);
  }
  return next;
}

export default function RegexTesterWidget() {
  const strings = useToolUi(REGEX_TESTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [pattern, setPattern] = useState(DEFAULT.pattern);
  const [flags, setFlags] = useState<Set<FlagKey>>(
    () => new Set(DEFAULT.flags.split("") as FlagKey[])
  );
  const [text, setText] = useState<string>(strings.librarySamples.isoDate);
  const [replacing, setReplacing] = useState(true);
  const [replacement, setReplacement] = useState(DEFAULT.replacement);
  const [library, setLibrary] = useState<LibraryPatternId | "">("isoDate");
  const [unicodeSets, setUnicodeSets] = useState(false);

  useEffect(() => {
    try {
      // biome-ignore lint/complexity/useRegexLiterals: a /…/v literal is a syntax error in browsers without the flag; the constructor is the feature test.
      new RegExp("", "v");
      setUnicodeSets(true);
    } catch {
      setUnicodeSets(false);
    }
  }, []);

  const flagString = flagsFrom(flags);
  const job: RegexJob = {
    pattern,
    flags: flagString,
    text: text.slice(0, MAX_TEXT),
    replacement: replacing ? replacement : null,
    maxMatches: MAX_MATCHES,
  };
  const [initial] = useState(() => runRegexJob(job));
  const { status, outcome, runHere } = useRegexRunner(job, initial, TIMEOUT_MS);
  const explanation = useMemo(
    () => explainRegex(pattern, flagString),
    [pattern, flagString]
  );

  function loadLibrary(id: LibraryPatternId | "") {
    setLibrary(id);
    if (id === "") {
      return;
    }
    const entry = libraryPattern(id);
    setPattern(entry.pattern);
    setFlags(new Set(entry.flags.split("") as FlagKey[]));
    setText(strings.librarySamples[id]);
    setReplacement(entry.replacement);
    setReplacing(entry.replacement !== "");
  }

  const highlights: TextMatch[] =
    outcome?.ok === true
      ? outcome.matches.map((match, index) => ({
          start: match.index,
          end: match.end,
          category: index % 2 === 0 ? "even" : "odd",
        }))
      : [];

  function statusMessage() {
    if (status === "timeout") {
      return (
        <ErrorNotice>
          {t(strings.timeout, { seconds: fmt(TIMEOUT_MS / 1000) })}
        </ErrorNotice>
      );
    }
    if (status === "unavailable") {
      return (
        <div className="space-y-3">
          <ErrorNotice>{strings.unavailable}</ErrorNotice>
          <button className={BUTTON_SECONDARY} onClick={runHere} type="button">
            {strings.runHere}
          </button>
        </div>
      );
    }
    if (outcome && !outcome.ok) {
      return (
        <ErrorNotice>
          {t(strings.invalid, { message: outcome.error })}
        </ErrorNotice>
      );
    }
    return null;
  }

  const matches = outcome?.ok ? outcome.matches : [];

  function summary(): string {
    if (status === "running") {
      return strings.running;
    }
    if (!outcome?.ok) {
      return "";
    }
    if (matches.length === 0) {
      return strings.noMatch;
    }
    return tPlural(strings.matchCount, locale, matches.length, {
      count: fmt(matches.length),
    }).trim();
  }

  return (
    <section className="space-y-6">
      <div className={`${CARD} space-y-4`}>
        <Field
          hint={strings.patternHint}
          id="rx-pattern"
          label={strings.patternLabel}
        >
          <input
            className={INPUT_MONO}
            id="rx-pattern"
            onChange={(event) => {
              setPattern(event.target.value);
              setLibrary("");
            }}
            spellCheck={false}
            type="text"
            value={pattern}
          />
        </Field>
        <div className="flex flex-wrap items-center gap-3">
          <code className="break-all rounded bg-muted px-2 py-1 font-mono text-foreground text-sm">
            {t(strings.literal, { literal: `/${pattern}/${flagString}` })}
          </code>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyPattern}
            small
            text={`/${pattern}/${flagString}`}
          />
        </div>
        <fieldset className="space-y-2">
          <legend className={LABEL}>{strings.flagsLabel}</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {FLAG_ORDER.filter((flag) => flag !== "v" || unicodeSets).map(
              (flag) => (
                <Checkbox
                  checked={flags.has(flag)}
                  key={flag}
                  label={
                    <span>
                      <code className="font-mono">{flag}</code>{" "}
                      {strings.flags[flag]}
                    </span>
                  }
                  onChange={(on) =>
                    setFlags((previous) => toggleFlag(previous, flag, on))
                  }
                />
              )
            )}
          </div>
        </fieldset>
        <Field id="rx-library" label={strings.libraryLabel}>
          <select
            className={SELECT}
            id="rx-library"
            onChange={(event) =>
              loadLibrary(event.target.value as LibraryPatternId | "")
            }
            value={library}
          >
            <option value="">{strings.libraryPlaceholder}</option>
            {REGEX_LIBRARY.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {strings.libraryNames[entry.id]}
              </option>
            ))}
          </select>
        </Field>
        {library === "" ? null : (
          <Notice>{strings.libraryNotes[library]}</Notice>
        )}
      </div>

      <TextAreaField
        id="rx-text"
        label={strings.textLabel}
        onChange={setText}
        rows={6}
        value={text}
      />
      {text.length > MAX_TEXT ? (
        <Notice>{t(strings.tooLong, { limit: fmt(MAX_TEXT) })}</Notice>
      ) : null}

      <div className="space-y-3">
        <Checkbox
          checked={replacing}
          label={strings.replaceToggle}
          onChange={setReplacing}
        />
        {replacing ? (
          <Field
            hint={strings.replaceHint}
            id="rx-replace"
            label={strings.replaceLabel}
          >
            <input
              className={INPUT_MONO}
              id="rx-replace"
              onChange={(event) => setReplacement(event.target.value)}
              spellCheck={false}
              type="text"
              value={replacement}
            />
          </Field>
        ) : null}
      </div>

      <div className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className={HINT}>
          {summary()}
        </p>
        {statusMessage()}
        {explanation.nestedQuantifier ? (
          <Notice>{strings.nested}</Notice>
        ) : null}
        <HighlightedText
          label={strings.highlightLabel}
          matches={highlights}
          text={text.slice(0, MAX_TEXT)}
          tones={{ even: "amber", odd: "sky" }}
        />
        {matches.length > 0 ? (
          <MatchTable
            fmt={fmt}
            matches={matches.slice(0, MAX_LISTED)}
            strings={strings}
          />
        ) : null}
        {matches.length > MAX_LISTED || (outcome?.ok && outcome.truncated) ? (
          <Notice>
            {t(strings.truncated, {
              count: fmt(Math.min(matches.length, MAX_LISTED)),
            })}
          </Notice>
        ) : null}
        {replacing && outcome?.ok && outcome.replaced !== null ? (
          <div className="space-y-2">
            <label className={LABEL} htmlFor="rx-replaced">
              {strings.replacedLabel}
            </label>
            <textarea
              className={TEXTAREA}
              id="rx-replaced"
              readOnly
              rows={5}
              value={outcome.replaced}
            />
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyReplaced}
              text={outcome.replaced}
            />
          </div>
        ) : null}
      </div>

      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.explanationHeading}</h3>
        {explanation.tokens.length === 0 ? (
          <Notice>{strings.explanationEmpty}</Notice>
        ) : (
          <ExplanationList
            fmt={fmt}
            locale={locale}
            strings={strings}
            tokens={explanation.tokens}
          />
        )}
      </div>
    </section>
  );
}
